import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Cloud, Building2, Factory, Shuffle } from 'lucide-react';

// ── Domain constants (mirror backend enums) ─────────────────────────────
const DEPLOYMENT_TYPES = [
  { id: 'cloud_saas', label: 'Cloud SaaS', short: 'SaaS', Icon: Cloud, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  { id: 'on_premise', label: 'On-Premises', short: 'On-Prem', Icon: Building2, color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  { id: 'customer_cloud', label: 'Customer Cloud', short: 'Cust-Cloud', Icon: Factory, color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  { id: 'hybrid', label: 'Hybrid', short: 'Hybrid', Icon: Shuffle, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
];
const DEPLOYMENT_MAP = Object.fromEntries(DEPLOYMENT_TYPES.map(d => [d.id, d]));

// Small inline component so every render-site renders the icon + label
// consistently. Used in tables, summary chips, and form pickers.
function DeploymentBadge({ deployment, sizeClasses = 'text-[11px] font-semibold px-2 py-0.5' }) {
  const dep = typeof deployment === 'string' ? DEPLOYMENT_MAP[deployment] : deployment;
  if (!dep) return null;
  const Icon = dep.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded ${sizeClasses} ${dep.color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      <span>{dep.short}</span>
    </span>
  );
}

const COMMERCIAL_MODELS = [
  { id: 'opex', label: 'OpEx' },
  { id: 'capex', label: 'CapEx' },
  { id: 'custom_quote', label: 'Custom Quote' },
];

const PRICING_UNITS = [
  { id: 'per_user_per_month', label: '/user/month' },
  { id: 'per_user_per_year', label: '/user/year' },
  { id: 'flat_license', label: 'flat license' },
  { id: 'per_agent_per_month', label: '/agent/month' },
  { id: 'per_minute', label: '/minute' },
  { id: 'per_transaction', label: '/transaction' },
  { id: 'one_time', label: 'one-time' },
  { id: 'custom', label: 'custom' },
];
const PRICING_UNIT_MAP = Object.fromEntries(PRICING_UNITS.map(u => [u.id, u.label]));

const ADDON_TYPES = [
  { id: 'professional_services', label: 'Professional Services', icon: '💼' },
  { id: 'amc', label: 'AMC', icon: '🔒' },
  { id: 'support', label: 'Support', icon: '🛠️' },
  { id: 'training', label: 'Training', icon: '🎓' },
  { id: 'one_time', label: 'One-time Fee', icon: '🎯' },
];
const ADDON_TYPE_MAP = Object.fromEntries(ADDON_TYPES.map(t => [t.id, t]));

const ADDON_PRICING = [
  { id: 'fixed', label: 'Fixed Price' },
  { id: 'per_day', label: 'Per Day' },
  { id: 'per_hour', label: 'Per Hour' },
  { id: 'percentage_of_license', label: '% of License' },
];

function fmtINR(v) {
  if (!v && v !== 0) return '—';
  return '₹' + Number(v).toLocaleString('en-IN');
}

// ────────────────────────────────────────────────── SKU row / form

function SKUCard({ sku, onEdit, onArchive, canEdit }) {
  const dep = DEPLOYMENT_MAP[sku.deployment_type] || DEPLOYMENT_MAP.cloud_saas;
  const isCustom = sku.commercial_model === 'custom_quote';
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800" data-testid={`sku-card-${sku.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <DeploymentBadge deployment={dep} />
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 uppercase tracking-wide">{sku.commercial_model}</span>
            {sku.tier && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{sku.tier}</span>}
            {!sku.active && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">Archived</span>}
          </div>
          <p className="font-mono text-xs text-slate-500 dark:text-slate-400 mb-2">{sku.sku_code || '(no code)'}</p>
          {isCustom ? (
            <p className="text-sm font-semibold text-purple-700 dark:text-purple-300">Contact pricing team for quote</p>
          ) : (
            <p className="text-lg font-bold text-slate-900 dark:text-white">
              {fmtINR(sku.list_price)} <span className="text-xs font-normal text-slate-500">{PRICING_UNIT_MAP[sku.pricing_unit] || ''}</span>
            </p>
          )}
          <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
            {sku.min_users > 0 && <span>Min {sku.min_users} users</span>}
            {sku.min_commitment_months > 0 && <span>Min {sku.min_commitment_months}mo commitment</span>}
            {sku.setup_fee > 0 && <span>+ {fmtINR(sku.setup_fee)} setup</span>}
          </div>
          {sku.includes && sku.includes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {sku.includes.map((inc, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded">{inc}</span>
              ))}
            </div>
          )}
          {sku.notes && <p className="text-[11px] text-slate-500 mt-2 italic">{sku.notes}</p>}
        </div>
        {canEdit && (
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => onEdit(sku)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded" title="Edit" data-testid={`sku-edit-${sku.id}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            {sku.active && (
              <button onClick={() => onArchive(sku)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title="Archive" data-testid={`sku-archive-${sku.id}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SKUForm({ productId, sku, onSaved, onCancel }) {
  const [f, setF] = useState({
    sku_code: sku?.sku_code || '',
    tier: sku?.tier || '',
    deployment_type: sku?.deployment_type || 'cloud_saas',
    commercial_model: sku?.commercial_model || 'opex',
    pricing_unit: sku?.pricing_unit || 'per_user_per_month',
    list_price: sku?.list_price ?? '',
    min_commitment_months: sku?.min_commitment_months ?? '',
    min_users: sku?.min_users ?? '',
    setup_fee: sku?.setup_fee ?? '',
    includes: (sku?.includes || []).join(', '),
    notes: sku?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const isCustom = f.commercial_model === 'custom_quote';

  async function save() {
    setSaving(true);
    const payload = {
      ...f,
      includes: f.includes.split(',').map(s => s.trim()).filter(Boolean),
      list_price: isCustom ? 0 : Number(f.list_price || 0),
      min_commitment_months: Number(f.min_commitment_months || 0),
      min_users: Number(f.min_users || 0),
      setup_fee: Number(f.setup_fee || 0),
    };
    try {
      if (sku?.id) {
        await axios.put(`${API}/products/skus/${sku.id}`, payload);
        toast.success('SKU updated');
      } else {
        await axios.post(`${API}/products/${productId}/skus`, payload);
        toast.success('SKU created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SKU');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="sku-form-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{sku?.id ? 'Edit SKU' : 'Add SKU'}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">SKU Code</label>
            <input value={f.sku_code} onChange={e => setF({...f, sku_code: e.target.value})} placeholder="e.g. INTALK-CRM-SAAS-GROWTH" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-code" />
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Tier</label>
            <input value={f.tier} onChange={e => setF({...f, tier: e.target.value})} placeholder="Starter / Growth / Enterprise" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-tier" />
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Deployment</label>
            <select value={f.deployment_type} onChange={e => setF({...f, deployment_type: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-deployment">
              {DEPLOYMENT_TYPES.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Commercial Model</label>
            <select value={f.commercial_model} onChange={e => setF({...f, commercial_model: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-commercial">
              {COMMERCIAL_MODELS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Pricing Unit</label>
            <select value={f.pricing_unit} onChange={e => setF({...f, pricing_unit: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-unit">
              {PRICING_UNITS.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
          {!isCustom && (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">List Price (INR)</label>
              <input type="number" value={f.list_price} onChange={e => setF({...f, list_price: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="sku-field-price" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Min Users</label>
            <input type="number" value={f.min_users} onChange={e => setF({...f, min_users: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Min Commitment (months)</label>
            <input type="number" value={f.min_commitment_months} onChange={e => setF({...f, min_commitment_months: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">One-time Setup Fee</label>
            <input type="number" value={f.setup_fee} onChange={e => setF({...f, setup_fee: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Includes (comma-separated)</label>
            <input value={f.includes} onChange={e => setF({...f, includes: e.target.value})} placeholder="Voice, Email, WhatsApp, Reports" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Notes</label>
            <textarea value={f.notes} onChange={e => setF({...f, notes: e.target.value})} rows={2} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50" data-testid="sku-form-save">{saving ? 'Saving…' : 'Save SKU'}</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────── Addon card / form

function AddonCard({ addon, onEdit, onArchive, canEdit }) {
  const t = ADDON_TYPE_MAP[addon.type] || { icon: '•', label: addon.type };
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800" data-testid={`addon-card-${addon.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-lg">{t.icon}</span>
            <span className="font-semibold text-sm text-slate-900 dark:text-white">{addon.name}</span>
            <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{t.label}</span>
            {addon.mandatory && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">Mandatory</span>}
            {addon.recurring && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">Recurring{addon.period ? ` · ${addon.period}` : ''}</span>}
            {!addon.active && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">Archived</span>}
          </div>
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            {addon.pricing_model === 'percentage_of_license'
              ? `${addon.percentage}% of license`
              : `${fmtINR(addon.amount)}${addon.pricing_model === 'per_day' ? ' /day' : addon.pricing_model === 'per_hour' ? ' /hour' : ''}`}
          </p>
          {addon.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{addon.description}</p>}
          {addon.applies_to_deployment?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {addon.applies_to_deployment.map(d => (
                <DeploymentBadge key={d} deployment={d} sizeClasses="text-[10px] px-1.5 py-0.5" />
              ))}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => onEdit(addon)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded" title="Edit" data-testid={`addon-edit-${addon.id}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            {addon.active && (
              <button onClick={() => onArchive(addon)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title="Archive" data-testid={`addon-archive-${addon.id}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AddonForm({ productId, addon, onSaved, onCancel }) {
  const [f, setF] = useState({
    type: addon?.type || 'professional_services',
    name: addon?.name || '',
    description: addon?.description || '',
    pricing_model: addon?.pricing_model || 'fixed',
    amount: addon?.amount ?? '',
    percentage: addon?.percentage ?? '',
    applies_to_deployment: addon?.applies_to_deployment || [],
    mandatory: !!addon?.mandatory,
    recurring: !!addon?.recurring,
    period: addon?.period || '',
  });
  const [saving, setSaving] = useState(false);
  const isPct = f.pricing_model === 'percentage_of_license';

  function toggleDep(dep) {
    setF(prev => ({ ...prev, applies_to_deployment: prev.applies_to_deployment.includes(dep)
      ? prev.applies_to_deployment.filter(d => d !== dep)
      : [...prev.applies_to_deployment, dep] }));
  }

  async function save() {
    setSaving(true);
    const payload = {
      ...f,
      amount: isPct ? 0 : Number(f.amount || 0),
      percentage: isPct ? Number(f.percentage || 0) : null,
    };
    try {
      if (addon?.id) {
        await axios.put(`${API}/products/addons/${addon.id}`, payload);
        toast.success('Add-on updated');
      } else {
        await axios.post(`${API}/products/${productId}/addons`, payload);
        toast.success('Add-on created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save add-on');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="addon-form-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{addon?.id ? 'Edit Add-on' : 'Add Add-on'}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Type</label>
            <select value={f.type} onChange={e => setF({...f, type: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="addon-field-type">
              {ADDON_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Pricing Model</label>
            <select value={f.pricing_model} onChange={e => setF({...f, pricing_model: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="addon-field-pricing">
              {ADDON_PRICING.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Name</label>
            <input value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="e.g. AMC — Enterprise" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="addon-field-name" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Description</label>
            <textarea value={f.description} onChange={e => setF({...f, description: e.target.value})} rows={2} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          {isPct ? (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Percentage (%)</label>
              <input type="number" step="0.1" value={f.percentage} onChange={e => setF({...f, percentage: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="addon-field-percentage" />
            </div>
          ) : (
            <div>
              <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Amount (INR)</label>
              <input type="number" value={f.amount} onChange={e => setF({...f, amount: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="addon-field-amount" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Period (e.g. annual)</label>
            <input value={f.period} onChange={e => setF({...f, period: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-600 dark:text-slate-300 font-medium mb-1 block">Applies to deployments</label>
            <div className="flex gap-2 flex-wrap">
              {DEPLOYMENT_TYPES.map(d => {
                const Icon = d.Icon;
                const active = f.applies_to_deployment.includes(d.id);
                return (
                  <button key={d.id} type="button" onClick={() => toggleDep(d.id)} className={'inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ' + (active ? d.color + ' border-transparent' : 'bg-white dark:bg-slate-700 text-slate-500 border-slate-300 dark:border-slate-600')}>
                    {Icon && <Icon className="w-3 h-3" />}
                    <span>{d.short}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2 flex gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300"><input type="checkbox" checked={f.mandatory} onChange={e => setF({...f, mandatory: e.target.checked})} /> Mandatory</label>
            <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300"><input type="checkbox" checked={f.recurring} onChange={e => setF({...f, recurring: e.target.checked})} /> Recurring</label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50" data-testid="addon-form-save">{saving ? 'Saving…' : 'Save Add-on'}</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────── Product detail modal

function ProductDetail({ productId, onClose, onChanged, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('skus');
  const [editingSku, setEditingSku] = useState(undefined); // undefined = closed, null = new, obj = edit
  const [editingAddon, setEditingAddon] = useState(undefined);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/products/${productId}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load product'))
      .finally(() => setLoading(false));
  }, [productId]);
  useEffect(load, [load]);

  async function archiveSku(sku) {
    if (!window.confirm(`Archive SKU ${sku.sku_code}?`)) return;
    try { await axios.delete(`${API}/products/skus/${sku.id}`); toast.success('SKU archived'); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to archive'); }
  }
  async function archiveAddon(addon) {
    if (!window.confirm(`Archive add-on ${addon.name}?`)) return;
    try { await axios.delete(`${API}/products/addons/${addon.id}`); toast.success('Add-on archived'); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to archive'); }
  }

  if (loading || !data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const p = data.product;
  const skus = data.skus || [];
  const addons = data.addons || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-40 p-4 overflow-y-auto" onClick={onClose} data-testid="product-detail-modal">
        <div className="bg-white dark:bg-slate-800 rounded-xl max-w-5xl w-full my-8" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">{p.name}</h2>
                {p.status === 'active' && <span className="text-[11px] px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold">● Active</span>}
                {p.status === 'beta' && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold">● Beta</span>}
                {p.status === 'retired' && <span className="text-[11px] px-2 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold">● Retired</span>}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                {p.product_line && <span>Product line: <b>{p.product_line}</b></span>}
                {p.category && <span>· {p.category}</span>}
                {p.tags?.map(t => <span key={t} className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700">#{t}</span>)}
              </div>
              {p.description && <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{p.description}</p>}
            </div>
            <button onClick={onClose} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" data-testid="product-detail-close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div className="px-6 pt-4 flex gap-1 border-b border-slate-200 dark:border-slate-700">
            <button onClick={() => setTab('skus')} className={'px-4 py-2 text-sm font-medium border-b-2 ' + (tab === 'skus' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-700')} data-testid="product-tab-skus">SKUs ({skus.length})</button>
            <button onClick={() => setTab('addons')} className={'px-4 py-2 text-sm font-medium border-b-2 ' + (tab === 'addons' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-700')} data-testid="product-tab-addons">Add-ons ({addons.length})</button>
            <button onClick={() => setTab('features')} className={'px-4 py-2 text-sm font-medium border-b-2 ' + (tab === 'features' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-700')} data-testid="product-tab-features">Features</button>
          </div>

          <div className="p-6">
            {tab === 'skus' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-slate-500">{skus.length} SKUs · {[...new Set(skus.map(s => s.deployment_type))].length} deployment type(s)</p>
                  {canEdit && <button onClick={() => setEditingSku(null)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="product-sku-add">+ Add SKU</button>}
                </div>
                {skus.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-sm">No SKUs yet. Add one to start selling this product.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {skus.map(s => <SKUCard key={s.id} sku={s} onEdit={setEditingSku} onArchive={archiveSku} canEdit={canEdit} />)}
                  </div>
                )}
              </div>
            )}
            {tab === 'addons' && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-slate-500">{addons.length} add-ons attached</p>
                  {canEdit && <button onClick={() => setEditingAddon(null)} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="product-addon-add">+ Add Add-on</button>}
                </div>
                {addons.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-sm">No add-ons yet. Attach AMC, professional services, or one-time fees.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {addons.map(a => <AddonCard key={a.id} addon={a} onEdit={setEditingAddon} onArchive={archiveAddon} canEdit={canEdit} />)}
                  </div>
                )}
              </div>
            )}
            {tab === 'features' && (
              <div>
                {p.features?.length > 0 ? (
                  <ul className="space-y-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">No features listed.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {editingSku !== undefined && (
        <SKUForm
          productId={productId}
          sku={editingSku}
          onSaved={() => { setEditingSku(undefined); load(); onChanged?.(); }}
          onCancel={() => setEditingSku(undefined)}
        />
      )}
      {editingAddon !== undefined && (
        <AddonForm
          productId={productId}
          addon={editingAddon}
          onSaved={() => { setEditingAddon(undefined); load(); onChanged?.(); }}
          onCancel={() => setEditingAddon(undefined)}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────── Product list card

function ProductCard({ product, onOpen }) {
  return (
    <div
      onClick={() => onOpen(product.id)}
      className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 bg-white dark:bg-slate-800 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-slate-900 dark:text-white truncate">{product.name}</h3>
          {product.product_line && <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{product.product_line}</p>}
        </div>
        {product.status === 'beta' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold flex-shrink-0">BETA</span>}
      </div>
      {product.description && <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">{product.description}</p>}
      <div className="flex flex-wrap gap-1 mb-3">
        {(product.deployments || []).map(d => (
          <DeploymentBadge key={d} deployment={d} sizeClasses="text-[10px] font-medium px-1.5 py-0.5" />
        ))}
        {product.deployments?.length === 0 && <span className="text-[10px] text-slate-400 italic">No SKUs yet</span>}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400">
        <span>{product.sku_count} SKUs · {product.addon_count} add-ons</span>
        {product.cheapest_sku && (
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            From {fmtINR(product.cheapest_sku.list_price)}
            <span className="text-[10px] text-slate-400 ml-1">{PRICING_UNIT_MAP[product.cheapest_sku.pricing_unit]}</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────── Main page

export default function ProductsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deploymentFilter, setDeploymentFilter] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', product_line: '', category: '', description: '' });
  const [detailId, setDetailId] = useState(null);

  const canEdit = !!permissions.can_create_products || user?.role === 'admin';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('active_only', activeOnly);
      if (search) params.set('search', search);
      const r = await axios.get(`${API}/products?${params}`);
      setProducts(r.data || []);
    } catch {
      toast.error('Failed to load products');
    } finally { setLoading(false); }
  }, [activeOnly, search]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => {
    axios.get(`${API}/auth/permissions`).then(r => setPermissions(r.data?.permissions || {})).catch(() => {});
  }, []);

  const filtered = deploymentFilter === 'all'
    ? products
    : products.filter(p => (p.deployments || []).includes(deploymentFilter));

  async function createProduct() {
    if (!newForm.name) { toast.error('Product name is required'); return; }
    try {
      await axios.post(`${API}/products`, newForm);
      toast.success('Product created');
      setShowNewProduct(false);
      setNewForm({ name: '', product_line: '', category: '', description: '' });
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  }

  return (
    <div className="p-6 space-y-4" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Products</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Catalog of products, SKUs (deployment × commercial model), and add-ons</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowNewProduct(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700" data-testid="product-create-btn">
            + New Product
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search products..."
          className="flex-1 min-w-[200px] max-w-sm px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
          data-testid="product-search"
        />
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setDeploymentFilter('all')} className={'text-xs px-2.5 py-1.5 rounded-full ' + (deploymentFilter === 'all' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300')} data-testid="filter-dep-all">All</button>
          {DEPLOYMENT_TYPES.map(d => {
            const Icon = d.Icon;
            const active = deploymentFilter === d.id;
            return (
              <button
                key={d.id}
                onClick={() => setDeploymentFilter(d.id)}
                className={'inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full ' + (active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : d.color)}
                data-testid={`filter-dep-${d.id}`}
              >
                {Icon && <Icon className="w-3 h-3" />}
                <span>{d.short}</span>
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-1 text-xs text-slate-500 ml-2">
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} data-testid="filter-active-only" />
          Active only
        </label>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="products-empty">
          <svg className="w-16 h-16 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
          <p className="text-sm">No products match your filters.</p>
          {canEdit && <button onClick={() => setShowNewProduct(true)} className="mt-3 text-sm text-blue-600 hover:underline">+ Create your first product</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map(p => <ProductCard key={p.id} product={p} onOpen={setDetailId} />)}
        </div>
      )}

      {/* New product modal */}
      {showNewProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-product-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">New Product</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Name *</label>
                <input value={newForm.name} onChange={e => setNewForm({...newForm, name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" data-testid="new-product-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Product Line</label>
                  <input value={newForm.product_line} onChange={e => setNewForm({...newForm, product_line: e.target.value})} placeholder="CRM / Voice / WhatsApp" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
                </div>
                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Category</label>
                  <input value={newForm.category} onChange={e => setNewForm({...newForm, category: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 font-medium">Description</label>
                <textarea value={newForm.description} onChange={e => setNewForm({...newForm, description: e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700" />
              </div>
              <p className="text-[11px] text-slate-500 italic">After creating the product, open it to add SKUs (deployment × commercial model) and add-ons (AMC, professional services).</p>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowNewProduct(false)} className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
              <button onClick={createProduct} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700" data-testid="new-product-save">Create</button>
            </div>
          </div>
        </div>
      )}

      {detailId && (
        <ProductDetail productId={detailId} onClose={() => setDetailId(null)} onChanged={fetchProducts} canEdit={canEdit} />
      )}
    </div>
  );
}
